import 'view/css/schedule.css';

import moment from 'moment';
import React, { useState, useEffect, useRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faFileExcel } from '@fortawesome/free-solid-svg-icons';

import Protected from 'component/Protected';
import Loading from 'component/Loading';
import SearchableList from 'component/dropdown/SearchableList';
import ModalScreen from 'component/ModalScreen';

import PopMessage from 'component/pop-msg/PopMessage';

import API from 'service/api';
import Util from 'service/util';
import Calendar from 'component/calendar/Calendar';

const MONTH_FETCH_DEBOUNCE_MS = 1500;

const mapSchedules = (list) => (list || []).map(x => {
  const m = moment.parseZone(x.l_date);
  const date = new Date(m.year(), m.month(), m.date());
  return {
    ...x,
    date,
    id: `${x.cd_license}-${x.loc_key}-${date.valueOf()}`,
    title: x.cd_license,
    allDay: 1
  };
});

const monthCacheKey = (workerId, month) => `${workerId}|${moment(month).format('YYYYMM')}`;

const collectWorkerSchedules = (cache, workerId) => {
  const prefix = `${workerId}|`;
  const all = [];
  cache.forEach((v, k) => {
    if (String(k).startsWith(prefix)) all.push(...v);
  });
  return all;
};

const WorkerScheduleView = (props) =>
{

  const [workers, setWorkers]           = useState(null);
  const [sel_worker, setSelectedWorker] = useState(null);
  const [schedules, setSchedules]       = useState([]);
  const [month, setMonth]               = useState(() => moment().startOf('month'));
  const [loading, setLoading]           = useState(false);
  const [modal, setShowModal]           = useState(false);

  const cacheRef    = useRef(new Map());
  const debounceRef = useRef(null);
  const reqIdRef    = useRef(0);

  useEffect(() => {
    API.getWorkerList().then(data => {
      const list = Array.isArray(data) ? data : [];
      setWorkers(list);
      if (list.length) setSelectedWorker(list[0]);
    });
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const clearDebounce = () => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const loadMonth = (worker, targetMonth, { immediate } = {}) => {
    if (!worker?.id) return;

    const key = monthCacheKey(worker.id, targetMonth);
    if (cacheRef.current.has(key)) {
      clearDebounce();
      reqIdRef.current += 1;
      setSchedules(collectWorkerSchedules(cacheRef.current, worker.id));
      setLoading(false);
      return;
    }

    const fetch = () => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      const ix = moment(targetMonth).format('YYYYMM');
      API.getWorkerSchedule(worker.id, ix).then(resp => {
        if (reqId !== reqIdRef.current) return;
        setLoading(false);
        if (resp.success)
        {
          cacheRef.current.set(key, mapSchedules(resp.msg));
          setSchedules(collectWorkerSchedules(cacheRef.current, worker.id));
        } else {
          setSchedules(collectWorkerSchedules(cacheRef.current, worker.id));
          PopMessage.show(resp.msg, '讀取失敗', 'danger');
        }
      });
    };

    if (immediate) {
      clearDebounce();
      setSchedules(collectWorkerSchedules(cacheRef.current, worker.id));
      fetch();
      return;
    }

    // Drop in-flight results from other months while the debounce waits.
    reqIdRef.current += 1;
    setLoading(true);
    setSchedules(collectWorkerSchedules(cacheRef.current, worker.id));
    clearDebounce();
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      fetch();
    }, MONTH_FETCH_DEBOUNCE_MS);
  };

  const onClickDay = (date, events) =>
  {
    if (!events) return;
    setShowModal({
      title: `服務行程 (${moment(date).format('DD/MM/YYYY')})`,
      events
    });
  }
  const closeModal = () => setShowModal(false);

  const onSelectWorker = (w) =>
  {
    setSelectedWorker(w);
    setSchedules([]);
    reqIdRef.current += 1;
    loadMonth(w, month, { immediate: true });
  }

  const onMonthChange = (nextMonth) =>
  {
    const m = moment(nextMonth).startOf('month');
    setMonth(m);
    if (!sel_worker) return;
    loadMonth(sel_worker, m);
  }

  const exportXls = () =>
  {
    if (!sel_worker) {
      PopMessage.show('請先選擇工人。', '警告', 'danger');
      return;
    }

    const ix = month.format('YYYYMM');
    API.exportWorkerSchedule(sel_worker.id, ix).then(resp =>
      Util.download(resp, `${sel_worker.name}的工作日程_${ix}.xlsx`)
    );
  } // export xlsx

  const _renderCalendar = () =>
  {
    if (!sel_worker) return '';
    return <Calendar
      events={schedules}
      month={month}
      loading={loading}
      onMonthChange={onMonthChange}
      onClickDay={onClickDay}
    />;
  }

  const _renderWorkerSelect = () =>
  {
    if (null === workers) return <Loading small />;
    if (!workers.length) return <span>沒有工人資料</span>;
    return <SearchableList value={sel_worker} onChange={onSelectWorker}
      items={ workers }
      filter={(w,v) => -1<`${w.id}||${w.worker_code}||${w.name}`.toUpperCase().indexOf(v.toUpperCase())}
      getTitle={w => w.cd || w.id }
      getDesc={w => w.name }
    />;
  }

  const _renderDayEventsModal = () =>
  {
    let M = modal;
    if (!M) return;
    return <ModalScreen title={M.title} show={true} onClose={closeModal}>
      <table className="table-sc-service">
        <thead>
          <tr>
            <th className="plan"    >計劃</th>
            <th className="license" >車牌</th>
            <th className="loc-key" >屋苑編號</th>
            <th className="loc-name">屋苑名稱</th>
            <th className="floor"   >樓層</th>
            <th className="psn"     >車位</th>
            <th className="waxing"  >打蠟</th>
            <th className="remarks" >備註</th>
          </tr>
        </thead>
        <tbody>
        { M.events.map(ev =>
          <tr key={ev.id} className="sc-service-item">
            <td className="plan"    >{ ev.srv_plan   }</td>
            <td className="license" >{ ev.cd_license }</td>
            <td className="loc-key" >{ ev.loc_key    }</td>
            <td className="loc-name">{ ev.loc_name   }</td>
            <td className="floor"   >{ ev.srv_floor  }</td>
            <td className="psn"     >{ ev.srv_psn    }</td>
            <td className="waxing"  >
              { !ev.waxing?'': <FontAwesomeIcon icon={faCheck} /> }
            </td>
            <td className="remarks">{ ev.remarks }</td>
          </tr>
        )}
        </tbody>
      </table>
    </ModalScreen>
  }

  return (
    <>
      <h1>日程 - 員工班期</h1>
      <div className="schedule-view">
        <p className="message blue">
          顯示所選月份的工作排程。切換月份後約 1.5 秒載入該月資料；已載入的月份會保留在本頁快取。
        </p>
        <label className="field-row">
          <strong>工人</strong>
          { _renderWorkerSelect() }
        </label>
        <Protected allow={['ADMIN','OPERATOR','VIEWER_SU','VIEWER_NIGHT']}>
          <button className="btn dark-green btn-export" onClick={exportXls}>
            <FontAwesomeIcon icon={faFileExcel} />
            <span>匯出</span>
          </button>
        </Protected>
        { loading && <Loading small /> }
      </div>
      <div>
        { _renderCalendar() }
      </div>
      { _renderDayEventsModal() }
    </>
  );

}


export default WorkerScheduleView;
